import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, PlayCircle, RefreshCw, CheckCircle2, AlertCircle, MailX, Search, AlertTriangle, Wrench } from "lucide-react";

const ACTION_LABEL = {
  reminder_d11: { label: "Day 11 reminder (3 left)", tone: "good" },
  reminder_d13: { label: "Day 13 reminder (1 left)", tone: "good" },
  reminder_d11_already_sent: { label: "Day 11 — already sent", tone: "muted" },
  reminder_d13_already_sent: { label: "Day 13 — already sent", tone: "muted" },
  trial_expired_downgrade: { label: "Expired — downgraded + emailed", tone: "warn" },
  trial_expired_downgrade_no_email: { label: "Expired — downgraded only", tone: "muted" },
  skipped_no_trial_end: { label: "Skipped — missing trial_ends_at", tone: "warn" },
  skipped_bad_trial_end: { label: "Skipped — unparseable trial_ends_at", tone: "warn" },
};
const TONE_CLS = {
  good: "bg-emerald-100 text-emerald-800",
  warn: "bg-amber-100 text-amber-800",
  muted: "bg-mm-navy/10 text-mm-navy/60",
};
const STATUS_PILL = {
  sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  stub: "bg-mm-navy/10 text-mm-navy/60",
  suppressed_unsubscribed: "bg-amber-100 text-amber-800",
};
const KIND_LABEL = {
  trial_reminder: "Trial reminder", trial_expired: "Trial expired", welcome: "Welcome",
  password_reset: "Password reset", weekly_report: "Weekly report", broadcast: "Broadcast",
};

const TrialEmailsPanel = () => {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [inspect, setInspect] = useState(null);
  const [loadingInspect, setLoadingInspect] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const r = await api.get("/admin/email-log?limit=200");
      setLog(r.data.events || []);
    } catch (err) {
      toast.error("Could not load email log");
    } finally { setLoadingLog(false); }
  };

  const loadInspect = async () => {
    setLoadingInspect(true);
    try {
      const r = await api.get("/admin/trial-lifecycle/inspect");
      setInspect(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load inspect data");
    } finally { setLoadingInspect(false); }
  };

  useEffect(() => { loadLog(); loadInspect(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/trial-lifecycle/run-now");
      setLastRun(r.data);
      const sent = r.data.reminders_sent || 0;
      toast.success(`Scanned ${r.data.scanned} trial users — attempted ${sent} reminder${sent === 1 ? "" : "s"} (${r.data.reminders_succeeded || 0} succeeded).`);
      setTimeout(() => { loadLog(); loadInspect(); }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  const previewBackfill = async () => {
    setBackfillRunning(true);
    try {
      const r = await api.post("/admin/trial-lifecycle/backfill-trial-end", { dry_run: true });
      setBackfillPreview(r.data);
      if (r.data.would_patch === 0) toast.info("Nothing to backfill — every trial user already has a valid trial_ends_at.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill scan failed");
    } finally { setBackfillRunning(false); }
  };

  const commitBackfill = async () => {
    if (!confirm(`Backfill trial_ends_at for ${backfillPreview.would_patch} user${backfillPreview.would_patch === 1 ? "" : "s"}? They will become eligible for reminders.`)) return;
    setBackfillRunning(true);
    try {
      const r = await api.post("/admin/trial-lifecycle/backfill-trial-end", { dry_run: false });
      toast.success(`Backfilled ${r.data.patched} user${r.data.patched === 1 ? "" : "s"}.`);
      setBackfillPreview(null);
      loadInspect();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill commit failed");
    } finally { setBackfillRunning(false); }
  };

  const filtered = kindFilter === "all" ? log : log.filter((e) => e.kind === kindFilter);

  return (
    <div className="mt-8 space-y-8" data-testid="admin-trial-emails-tab">
      {/* Run trigger + funnel report */}
      <div className="bg-white border border-mm-navy/10 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Lifecycle</div>
            <h2 className="font-heading text-xl font-black text-mm-navy mt-0.5 flex items-center gap-2">
              <Mail size={18} strokeWidth={2.5} /> Trial email check
            </h2>
            <p className="text-sm text-mm-navy/70 mt-1 max-w-2xl">
              Auto-fires daily at <strong>9:00 AM Eastern</strong>. Day 11 reminder sends with 3 days left; Day 13 sends with 1 day left. After day 14, users are downgraded to Free and a final "trial ended" email goes out.
            </p>
          </div>
          <button onClick={runNow} disabled={running} className="btn-trail btn-gold text-sm shrink-0" data-testid="trial-email-run-now-btn">
            <PlayCircle size={14} strokeWidth={2.5} /> {running ? "Running…" : "Run Trial Email Check Now"}
          </button>
        </div>

        {lastRun && (
          <div className="mt-5 rounded-xl bg-mm-parchment border border-mm-navy/10 p-4" data-testid="trial-email-last-run">
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-3">Funnel — last run @ {(lastRun.ran_at || "").replace("T", " ").slice(0, 16)}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center" data-testid="trial-funnel-stats">
              <Stat label="Scanned" value={lastRun.scanned} />
              <Stat label="Have trial_ends_at" value={lastRun.with_trial_ends_at} />
              <Stat label="Parseable" value={lastRun.parseable_dates} />
              <Stat label="In d11 window" value={lastRun.d11_window} accent={lastRun.d11_window > 0} />
              <Stat label="In d13 window" value={lastRun.d13_window} accent={lastRun.d13_window > 0} />
              <Stat label="Expired" value={lastRun.expired} />
              <Stat label="Reminders attempted" value={lastRun.reminders_sent} accent={lastRun.reminders_sent > 0} />
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
              <SmallStat label="Succeeded" value={lastRun.reminders_succeeded} tone="good" />
              <SmallStat label="Failed" value={lastRun.reminders_failed} tone={lastRun.reminders_failed > 0 ? "bad" : "muted"} />
              <SmallStat label="Downgraded" value={lastRun.downgraded} tone="muted" />
              <SmallStat label="Expired emails" value={lastRun.expired_emails_sent} tone="muted" />
            </div>
            {!!lastRun.details?.length && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">Per-user breakdown</div>
                <ul className="space-y-1.5 text-sm max-h-72 overflow-y-auto pr-1" data-testid="trial-email-details-list">
                  {lastRun.details.map((d, i) => {
                    const meta = ACTION_LABEL[d.action] || { label: d.action, tone: "muted" };
                    const Icon = meta.tone === "good" ? CheckCircle2 : meta.tone === "warn" ? AlertCircle : MailX;
                    return (
                      <li key={i} className="flex items-start gap-2 text-mm-navy/85">
                        <Icon size={14} strokeWidth={2.5} className={`mt-0.5 ${meta.tone === "good" ? "text-emerald-600" : meta.tone === "warn" ? "text-amber-600" : "text-mm-navy/35"}`} />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-mono text-xs">{d.email}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest ${TONE_CLS[meta.tone]}`}>{meta.label}</span>
                            {typeof d.days_remaining === "number" && <span className="text-xs text-mm-navy/50">· {d.days_remaining}d left</span>}
                            {d.ok === false && <span className="text-xs text-red-600 font-bold">FAILED</span>}
                          </div>
                          {d.reason && <div className="text-[11px] text-mm-navy/55 mt-0.5">{d.reason}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Trial user inspector */}
      <div className="bg-white border border-mm-navy/10 rounded-2xl">
        <div className="p-5 border-b border-mm-navy/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-black text-mm-navy flex items-center gap-2">
              <Search size={16} strokeWidth={2.5} /> Trial user inspector
            </h3>
            <p className="text-xs text-mm-navy/60 mt-0.5">
              Live view of every user with <code className="font-mono text-[11px] bg-mm-parchment px-1 rounded">plan = "trial"</code> — see exactly why each one is or isn't getting reminders.
            </p>
          </div>
          <button onClick={loadInspect} disabled={loadingInspect} className="inline-flex items-center gap-1.5 text-xs font-bold text-mm-steel hover:text-mm-navy" data-testid="trial-inspect-refresh-btn">
            <RefreshCw size={12} strokeWidth={2.5} className={loadingInspect ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {inspect?.summary && (
          <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border-b border-mm-navy/10 bg-mm-parchment text-center" data-testid="trial-inspect-summary">
            <SmallStat label="Total" value={inspect.summary.total_trial_users} tone="muted" />
            <SmallStat label="Missing end-date" value={inspect.summary.missing_trial_ends_at} tone={inspect.summary.missing_trial_ends_at > 0 ? "bad" : "muted"} />
            <SmallStat label="Unparseable" value={inspect.summary.unparseable_trial_ends_at} tone={inspect.summary.unparseable_trial_ends_at > 0 ? "bad" : "muted"} />
            <SmallStat label="In d11 window" value={inspect.summary.in_d11_window} tone={inspect.summary.in_d11_window > 0 ? "good" : "muted"} />
            <SmallStat label="In d13 window" value={inspect.summary.in_d13_window} tone={inspect.summary.in_d13_window > 0 ? "good" : "muted"} />
            <SmallStat label="Expired" value={inspect.summary.expired} tone="muted" />
          </div>
        )}

        {/* Backfill widget — only show if there ARE missing dates */}
        {inspect?.summary?.missing_trial_ends_at > 0 && (
          <div className="px-5 py-4 border-b border-mm-navy/10 bg-amber-50" data-testid="trial-backfill-panel">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} strokeWidth={2.5} className="text-amber-700" />
                  <span className="text-xs uppercase tracking-widest font-bold text-amber-700">Missing trial_ends_at</span>
                </div>
                <p className="text-sm text-mm-navy mt-1">
                  <strong>{inspect.summary.missing_trial_ends_at}</strong> trial user{inspect.summary.missing_trial_ends_at === 1 ? "" : "s"} have no <code className="font-mono text-[11px] bg-white px-1 rounded">trial_ends_at</code> set, so they never qualify for reminders. You can backfill them as <code className="font-mono text-[11px] bg-white px-1 rounded">created_at + 14 days</code>.
                </p>
              </div>
              <button onClick={previewBackfill} disabled={backfillRunning} className="btn-trail btn-outline-navy text-sm shrink-0" data-testid="trial-backfill-preview-btn">
                <Wrench size={14} strokeWidth={2.5} /> {backfillRunning ? "Scanning…" : "Preview backfill"}
              </button>
            </div>
            {backfillPreview && (
              <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 text-sm" data-testid="trial-backfill-preview">
                <div className="font-bold text-mm-navy">Would patch <span data-testid="trial-backfill-would-patch">{backfillPreview.would_patch}</span> user{backfillPreview.would_patch === 1 ? "" : "s"} · Skip {backfillPreview.skipped} (already expired or invalid created_at)</div>
                {!!backfillPreview.candidates?.length && (
                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto text-xs">
                    {backfillPreview.candidates.map((c) => (
                      <li key={c.id} className="font-mono">{c.email} → ends {c.new_trial_ends_at.slice(0, 10)}</li>
                    ))}
                  </ul>
                )}
                {backfillPreview.would_patch > 0 && (
                  <button onClick={commitBackfill} disabled={backfillRunning} className="btn-trail btn-gold text-xs mt-3" data-testid="trial-backfill-commit-btn">
                    Commit backfill ({backfillPreview.would_patch})
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="overflow-x-auto" data-testid="admin-trial-inspect-table">
          <table className="w-full text-sm">
            <thead className="bg-mm-parchment text-mm-navy">
              <tr className="text-left">
                <Th>Email</Th><Th>Account created</Th><Th>Trial ends</Th><Th>Days left</Th><Th>Auth</Th><Th>Reminders sent</Th><Th>Why no email?</Th>
              </tr>
            </thead>
            <tbody>
              {(inspect?.users || []).map((u) => (
                <tr key={u.id} className="border-t border-mm-navy/10 align-top" data-testid={`trial-inspect-row-${u.id}`}>
                  <Td>
                    <div className="font-mono text-xs">{u.email}</div>
                    <div className="text-[10px] text-mm-navy/45">{u.name}{u.is_admin ? " · ADMIN" : ""}</div>
                  </Td>
                  <Td className="text-xs text-mm-navy/65 whitespace-nowrap">{(u.account_created || "").slice(0, 10)}</Td>
                  <Td className="text-xs whitespace-nowrap">
                    {u.trial_ends_at ? <span className="font-mono">{u.trial_ends_at.slice(0, 16).replace("T", " ")}</span> : <span className="text-red-600 font-bold">MISSING</span>}
                  </Td>
                  <Td className="text-xs">
                    {u.days_remaining === null || u.days_remaining === undefined ? "—" :
                     u.days_remaining === -1 ? <span className="text-mm-navy/55">expired</span> :
                     <span className={u.days_remaining === 3 || u.days_remaining === 1 ? "font-bold text-emerald-700" : ""}>{u.days_remaining}d</span>}
                  </Td>
                  <Td className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">{u.auth_provider}</Td>
                  <Td className="text-xs">{(u.trial_reminders_sent || []).join(", ") || "—"}</Td>
                  <Td className="text-xs text-mm-navy/70 max-w-md">{u.disqualifier || <span className="text-emerald-700 font-bold">eligible</span>}</Td>
                </tr>
              ))}
              {!inspect?.users?.length && !loadingInspect && (
                <tr><Td colSpan={7} className="text-center text-mm-navy/55 italic py-6">No trial users in the database.</Td></tr>
              )}
              {loadingInspect && !inspect && (
                <tr><Td colSpan={7} className="text-center text-mm-navy/55 py-6">Loading…</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Email log */}
      <div className="bg-white border border-mm-navy/10 rounded-2xl">
        <div className="p-5 border-b border-mm-navy/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-black text-mm-navy flex items-center gap-2">
              <Mail size={16} strokeWidth={2.5} /> Email log
            </h3>
            <p className="text-xs text-mm-navy/60 mt-0.5">Last 200 email attempts. Updated live as the system sends.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="text-xs bg-white border border-mm-navy/15 rounded-lg px-2 py-1.5 font-bold text-mm-navy" data-testid="email-log-kind-filter">
              <option value="all">All kinds</option>
              <option value="trial_reminder">Trial reminders</option>
              <option value="trial_expired">Trial expired</option>
              <option value="welcome">Welcome</option>
              <option value="password_reset">Password reset</option>
              <option value="weekly_report">Weekly report</option>
              <option value="broadcast">Broadcast</option>
            </select>
            <button onClick={loadLog} disabled={loadingLog} className="inline-flex items-center gap-1.5 text-xs font-bold text-mm-steel hover:text-mm-navy" data-testid="email-log-refresh-btn">
              <RefreshCw size={12} strokeWidth={2.5} className={loadingLog ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-testid="admin-email-log-table">
          <table className="w-full text-sm">
            <thead className="bg-mm-parchment text-mm-navy">
              <tr className="text-left">
                <Th>Sent</Th><Th>Recipient</Th><Th>Kind</Th><Th>Subject</Th><Th>Status</Th><Th>Error</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id || `${e.to}-${e.created_at}`} className="border-t border-mm-navy/10 align-top">
                  <Td className="text-xs text-mm-navy/65 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</Td>
                  <Td className="font-mono text-xs">{e.to}</Td>
                  <Td>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">{KIND_LABEL[e.kind] || e.kind}</span>
                  </Td>
                  <Td className="max-w-md truncate text-mm-navy/85">{e.subject}</Td>
                  <Td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_PILL[e.status] || "bg-mm-navy/10 text-mm-navy"}`}>{e.status}</span></Td>
                  <Td className="max-w-xs text-xs text-red-700">{e.error || ""}</Td>
                </tr>
              ))}
              {!filtered.length && !loadingLog && (
                <tr><Td colSpan={6} className="text-center text-mm-navy/55 italic py-6">No events match this filter.</Td></tr>
              )}
              {loadingLog && !filtered.length && (
                <tr><Td colSpan={6} className="text-center text-mm-navy/55 py-6">Loading…</Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`p-2 rounded-lg ${accent ? "bg-mm-navy text-white" : "bg-white border border-mm-navy/10"}`}>
    <div className={`text-[9px] uppercase tracking-widest font-bold ${accent ? "text-mm-gold" : "text-mm-steel"}`}>{label}</div>
    <div className={`font-heading text-xl font-black ${accent ? "text-white" : "text-mm-navy"}`}>{value ?? 0}</div>
  </div>
);
const SmallStat = ({ label, value, tone = "muted" }) => {
  const cls = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-700" : "text-mm-navy/70";
  return (
    <div className="bg-white border border-mm-navy/10 rounded px-2 py-1">
      <div className="text-[9px] uppercase tracking-widest font-bold text-mm-steel">{label}</div>
      <div className={`font-bold text-base ${cls}`}>{value ?? 0}</div>
    </div>
  );
};
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">{children}</th>;
const Td = ({ children, colSpan, className = "" }) => <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>;

export default TrialEmailsPanel;
